<template>
    <div class="p-6">
        <div id="card-element" class="mb-4"></div>
        <button @click="processPayment"
            :disabled="isCreatingPaymentIntent"
            class="btn rounded-full bg-echoes-primary hover:bg-echoes-primary/80 border-echoes-primary hover:border-echoes-primary/80 text-white w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            <svg v-if="isCreatingPaymentIntent" class="size-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            <i v-else class="mdi mdi-credit-card"></i> 
            {{ isCreatingPaymentIntent ? 'Creating payment...' : `Pay ${formatPrice(getStripePayment.amount, 'USD')}` }}
        </button>
    </div>
    
</template>

<script setup>
import { defineProps, computed, onMounted, ref, nextTick, defineEmits} from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { useStorage } from '@/composables/use-storage';
import axios from '@/plugins/axios';
import { useSystemSettings } from '@/composables/use-system-settings';
import { useToast } from '@/composables/use-toast';

const { getUserInfo } = useStorage();
const { getSystemSetting } = useSystemSettings();
const { addToast } = useToast();

const userInfo = ref(getUserInfo());

const stripe = ref(null);
const elements = ref(null);
const cardElement = ref(null);
const isCreatingPaymentIntent = ref(false);
const emit = defineEmits(['payment-success']);

const stripePromise = computed(() => {
    const stripePublicKey = getSystemSetting('STRIPE_PUBLIC_KEY')
    return stripePublicKey ? loadStripe(stripePublicKey) : null;
});


const props = defineProps({
    payments: {
        type: Array,
        required: true
    },
    listingId: {
        type: Number,
        required: true
    },
    bidId: {
        type: Number,
        required: false,
        default: null
    },
    evalResponseFile: {
        type: String,
        required: false,
        default: null
    },
    version: {
        type: Number,
        required: false,
        default: null
    }
})

const getStripePayment = computed(() => {
    return props.payments.find(payment => payment.method === 'stripe');
})

const getCreditsUsed = computed(() => {
    return props.payments.find(payment => payment.method === 'credits') ?? null;
})

const formatPrice = (price, currency = 'USDC') => {
    const numPrice = Number(price);
    if (Number.isInteger(numPrice)) {
        return numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + currency;
    }
    return numPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency;
};

const initializeStripe = async () => {
    try {
        isCreatingPaymentIntent.value = true;
        
        if (!stripe.value) {
            stripe.value = await stripePromise.value;
        }
        
        const response = await axios.post('/api/sales/payment_intent', {
            amount: getStripePayment.value.amount,
            currency: 'usd',
            listing_id: props.listingId,
            buyer_id: userInfo.value.id,
            credits_used: getCreditsUsed.value?.amount ?? 0,
            bid_id: props.bidId,
            model_response_file: props.evalResponseFile ?? null,
            version: props.version ?? null
        });

        const { clientSecret} = response.data;
        
        const savedTheme = localStorage.getItem('theme') || 'dark';
        const options = {
            clientSecret,
            appearance: {
                theme: 'night',
                variables: {
                    colorPrimary: '#FE3E01',
                    colorBackground: savedTheme === 'dark' ? '#232323' : '#ffffff',
                    colorText: savedTheme === 'dark' ? '#ffffff' : '#424770',
                    colorDanger: '#df1b41',
                    borderRadius: '8px',
                }
            },
        };

        elements.value = stripe.value.elements(options);

        nextTick(() => {
            if (document.getElementById('card-element')) {
                cardElement.value = elements.value.create('payment');
                cardElement.value.mount('#card-element');
            }
        });
    } catch (error) {
        console.error('Error initializing Stripe:', error);
        addToast('Error initializing payment. Please try again.', 'error');
    } finally {
        isCreatingPaymentIntent.value = false;
    }
};

const processPayment = async () => {
    try {
        if(getStripePayment.value.amount <= 0){
            addToast('Please select a payment method', 'error');
            return;
        }
        if (!stripe.value || !elements.value) {
            throw new Error('Stripe was not initialized correctly');
        }

        const { error: submitError } = await elements.value.submit();
        if (submitError) {
            throw submitError;
        }

        const result = await stripe.value.confirmPayment({
            elements: elements.value,
            confirmParams: {
                return_url: `${globalThis.location.origin}/checkout-confirmation`,
            }
        });

        if (result.error) {
            throw result.error;
        }

        emit('payment-success');
    } catch (error) {
        console.error('Error processing payment:', error);
        addToast('Error processing payment: ' + (error.message || 'Try again'), 'error');
        emit('payment-error', error);
    }
};

onMounted(() => {
    initializeStripe();
});


</script>